import { useState, useCallback } from 'react';
import { ProfileFormData, FormErrors, FormState } from '../types/profileForm.types';

// Mock initial data - replace with actual API call
const mockInitialData: ProfileFormData = {
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  dateOfBirth: '',
  postalCode: '',
  state: '',
  description: '',
  profilePhoto: undefined,
  skills: '',
  skillsChips: [],
  skillEntries: [],
};

export const useProfileForm = () => {
  const [formState, setFormState] = useState<FormState>({
    data: mockInitialData,
    errors: {},
    loading: false,
    isDirty: false,
    isSubmitting: false,
  });

  const updateField = useCallback((field: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: typeof value === 'function' ? value((prev.data as any)[field]) : value,
      },
      isDirty: true,
      // Clear error for this field when user starts typing
      errors: {
        ...prev.errors,
        [field]: undefined,
      },
    }));
  }, []);

  const setFieldError = useCallback((field: string, error: string) => {
    setFormState(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: error,
      },
    }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFormState(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: undefined,
      },
    }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setFormState(prev => ({
      ...prev,
      loading,
    }));
  }, []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setFormState(prev => ({
      ...prev,
      isSubmitting,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState({
      data: mockInitialData,
      errors: {},
      loading: false,
      isDirty: false,
      isSubmitting: false,
    });
  }, []);

  const updateFormData = useCallback((newData: Partial<ProfileFormData>) => {
    setFormState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ...newData,
      },
      isDirty: true,
    }));
  }, []);

  const replaceFormData = useCallback((newData: Partial<ProfileFormData>) => {
    setFormState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ...newData,
      },
      isDirty: false,
    }));
  }, []);

  const setErrors = useCallback((errors: FormErrors) => {
    setFormState(prev => ({
      ...prev,
      errors,
    }));
  }, []);

  return {
    formData: formState.data,
    errors: formState.errors,
    loading: formState.loading,
    isDirty: formState.isDirty,
    isSubmitting: formState.isSubmitting,
    updateField,
    setFieldError,
    clearFieldError,
    setLoading,
    setSubmitting,
    resetForm,
    updateFormData,
    replaceFormData,
    setErrors,
  };
};